import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import { Injectable } from '@angular/core';

import { catchError, firstValueFrom, map, Observable, throwError } from 'rxjs';
import { LocalStorageService } from '../local-storage/local-storage.service';
import { environment } from '../../../environments/environment';
import { SmartQueueRegisterLog } from '../../interfaces/smart-queue-register-log';
import { Router } from '@angular/router';
import { unidadePermitida } from '../../utils/unidades-permitidas.util';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  protected apiVersion = environment.apiVersion;
  protected apiURL = environment.apiUrl;

  private userProfile!: string;

  ticketWindow!: string;
  unit!: number;

  constructor(
    protected httpClient: HttpClient,
    private localStorageService: LocalStorageService,
    private router: Router,
  ) {
    this.apiURL = this.apiURL + '/v' + this.apiVersion;

    if (localStorage.getItem(this.ticketWindow)) {
      this.setTicketWindow(localStorage.getItem(this.ticketWindow) as string);
    }
  }

  setTicketWindow(valor: string): void {
    localStorage.setItem(this.ticketWindow, valor);
  }

  getTicketWindow(): string {
    return localStorage.getItem(this.ticketWindow) || '';
  }

  public async login(login: string, password: string): Promise<any> {
    return await firstValueFrom(
      this.httpClient
        .post(`${this.apiURL}/Auth/`, { login, password })
        .pipe(catchError(this.handleError)),
    );
  }

  public async logout(): Promise<any> {
    return await firstValueFrom(
      this.httpClient
        .delete(`${this.apiURL}/Auth/logout`)
        .pipe(catchError(this.handleError)),
    );
  }

  public async registerUserLog(): Promise<any> {
    const userLog = {
      username: this.localStorageService.get('login'),
      establishmentId: Number(this.localStorageService.get('currentUnit')),
      computerName: this.localStorageService.get('currentMachine'),
      ticketWindowId: this.localStorageService.get('idTicketWindow'),
    } as SmartQueueRegisterLog;

    return await firstValueFrom(
      this.httpClient
        .put(`${this.apiURL}/User/save-user-log`, userLog)
        .pipe(catchError(this.handleError)),
    );
  }

  public async getUserByLogin(login: string): Promise<any> {
    return await firstValueFrom(
      this.httpClient.get<any>(`${this.apiURL}/User/${login}`).pipe(
        map((response) => response.data[0]),
        catchError(this.handleError),
      ),
    );
  }

  public async getUnits(login: string, cdEmpresa: string): Promise<any> {
    const query = cdEmpresa
      ? `?cdEmpresa=${encodeURIComponent(cdEmpresa)}`
      : '';
    const resposta = await firstValueFrom(
      this.httpClient
        .get<any>(`${this.apiURL}/User/${login}/establishments${query}`)
        .pipe(catchError(this.handleError)),
    );
    if (Array.isArray(resposta?.data)) {
      resposta.data = resposta.data.filter((u: { id: number }) => unidadePermitida(u.id));
    }
    return resposta;
  }

  public async getTicketsWindows(unit_id: string): Promise<Observable<any>> {
    return this.httpClient
      .get<any>(`${this.apiURL}/User/${unit_id}/ticketWindows`)
      .pipe(catchError(this.handleError));
  }

  public async setQueueToComputer(
    computerId: number,
    clinicId: number,
    queueToRegister: number,
    computerName: string,
  ): Promise<any> {
    return await firstValueFrom(
      this.httpClient.put<any>(`${this.apiURL}/User/set-queue`, {
        computerId,
        clinicId,
        queueToRegister,
        computerName,
      }),
    );
  }

  public async setManualCall(
    manualCall: boolean,
    clinicId: number,
    computerId: number,
    computerName: string,
  ): Promise<any> {
    return await firstValueFrom(
      this.httpClient.put<any>(`${this.apiURL}/User/set-manual-call`, {
        manualCall,
        clinicId,
        computerId,
        computerName,
      }),
    );
  }

  public async getTicketWindowPercent(clinicId: number): Promise<number> {
    return await firstValueFrom(
      this.httpClient.get<number>(
        `${this.apiURL}/User/get-percent-tw/${clinicId}`,
      ),
    );
  }

  public async getClinicProportion(clinicId: number): Promise<any> {
    return await firstValueFrom(
      this.httpClient
        .get<any>(`${this.apiURL}/User/get-clinic-proportion/${clinicId}`)
        .pipe(
          map((response) => response.data),
          catchError(this.handleError),
        ),
    );
  }

  public async setClinicProportion(
    clinicId: number,
    qtPriority: number,
    qtNormal: number,
  ): Promise<any> {
    return await firstValueFrom(
      this.httpClient
        .put<any>(`${this.apiURL}/User/set-clinic-proportion`, {
          clinicId,
          qtPriority,
          qtNormal,
        })
        .pipe(catchError(this.handleError)),
    );
  }

  private handleError(error: HttpErrorResponse) {
    let errorMessage = 'Unknown error occurred';

    if (error.error instanceof ErrorEvent) {
      // Erro do lado do cliente
      errorMessage = `Error: ${error.error.message}`;
    } else {
      // Erro do lado do servidor
      switch (error.status) {
        case 400:
          errorMessage = `Bad Request: Verifique os parâmetros enviados.`;
          break;
        case 401:
          errorMessage = `Unauthorized: Requisição não autorizada.`;
          break;
        case 404:
          errorMessage = `Not Found: O recurso solicitado não foi encontrado.`;
          break;
        case 500:
          errorMessage = `Internal Server Error: Houve um problema no servidor. Tente novamente mais tarde.`;
          break;
        default:
          errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
          break;
      }
    }
    console.error(errorMessage);
    return throwError(() => errorMessage);
  }

  changeExpiresAtCookie(cookieName: string, expiresAt: string) {
    const token = this.localStorageService.get('token');
    if (token) {
      this.localStorageService.set('tokenExpiresAt', expiresAt);
    }
  }

  public setAuth({ name, token, expiresAt }: any) {
    this.localStorageService.set('login', name);
    this.localStorageService.set('token', token);
    this.localStorageService.set('tokenExpiresAt', expiresAt);
  }

  public removeAuth() {
    this.localStorageService.remove('login');
    this.localStorageService.remove('token');
    this.localStorageService.remove('tokenExpiresAt');
  }

  public getTokenAuth() {
    const token = this.localStorageService.get('token');
    const expiresAt = this.localStorageService.get('tokenExpiresAt');

    if (expiresAt && new Date(expiresAt) < new Date()) {
      this.removeAuth();
      this.router.navigate(['/login']);
      return null;
    }

    if (!token && location.pathname != '/login') {
      this.router.navigate(['/login']);
    }

    return token;
  }
}
